import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {Override} from 'util/Types';

import {
  ANALYSIS_OUTPUT_STEP_TYPES,
  NON_EXECUTABLE_STEP_TYPES,
  NON_REFRESHABLE_STEP_TYPES,
} from './AnalysisToolConstants';
import {GetAnalysis} from './graphql/__generated/GetAnalysis';

// These generated types must be overriden with `DataFrame` interfaces, and should not be exported.
type GeneratedAnalysisOrInaccessibleGraphQL = GetAnalysis['analysis'];
type GeneratedAnalysisGraphQL = ExtractByTypename<GeneratedAnalysisOrInaccessibleGraphQL, 'AnalysisNode'>;
type GeneratedGetAnalysisExecution = GeneratedAnalysisGraphQL['execution'];
type GeneratedGetAnalysisPipelineStep = GeneratedAnalysisGraphQL['execution']['steps'][number];
type GeneratedGetAnalysisStepData = GeneratedGetAnalysisPipelineStep['outputStepDatas'][number];
type GeneratedGetAnalysisDataFrame =
  GeneratedGetAnalysisPipelineStep['outputStepDatas'][number]['dataFrames'][number];

// These generated types do not need to be overriden before being re-exported.
export type GetAnalysisChart = ExtractByTypename<
  GetAnalysisPipelineStep['outputStepDatas'][number]['charts'][number],
  'ChartNode'
>;
export type GetAnalysisScratchFile = ExtractByTypename<
  GetAnalysisPipelineStep['outputStepDatas'][number]['scratchFiles'][number],
  'ScratchFileNode'
>;

/** @deprecated Use `PipelineStepConfig` from `PipelineTypes.ts` */
export type GetAnalysisPipelineStepConfig = GeneratedGetAnalysisPipelineStep['config'];
/** @deprecated Use `PipelineStep` from `PipelineTypes.ts` */
export type GetAnalysisPipelineStepDefinition = GeneratedGetAnalysisPipelineStep['definition'];
/** @deprecated Use `PipelineEdge` from `PipelineTypes.ts` */
export type GetAnalysisIncomingEdge = GeneratedGetAnalysisPipelineStep['definition']['incomingEdges'][number];
/** @deprecated Use `PipelineEdge` from `PipelineTypes.ts` */
export type GetAnalysisOutgoingEdge = GeneratedGetAnalysisPipelineStep['definition']['outgoingEdges'][number];

// These exported types are overriden with `DataFrame` interfaces.
export type GetAnalysisDataFrame = Override<
  GeneratedGetAnalysisDataFrame,
  {allColumnsJson: Array<DataFrameColumnDef>}
>;

/** @deprecated Use `PipelineStepData` from `PipelineTypes.ts` */
export type GetAnalysisStepData = Override<
  GeneratedGetAnalysisStepData,
  {dataFrames: Array<GetAnalysisDataFrame>}
>;

/** @deprecated Use `PipelineStep` from `PipelineTypes.ts` */
export type GetAnalysisPipelineStep = Override<
  GeneratedGetAnalysisPipelineStep,
  {inputStepDatas: Array<GetAnalysisStepData>; outputStepDatas: Array<GetAnalysisStepData>}
>;

/** @deprecated Use `PipelineStep` from `PipelineTypes.ts` */
export type GetAnalysisExecution = Override<
  GeneratedGetAnalysisExecution,
  {steps: Array<GetAnalysisPipelineStep>}
>;

export type AnalysisGraphQL = Override<GeneratedAnalysisGraphQL, {execution: GetAnalysisExecution}>;
export type GetAnalysisInaccessibleInfoNode = ExtractByTypename<
  GeneratedAnalysisOrInaccessibleGraphQL,
  'InaccessibleInfoNode'
>;
export type AnalysisOrInaccessibleGraphQL = AnalysisGraphQL | GetAnalysisInaccessibleInfoNode;

export function isAnalysisGraphQL(
  pipeline: PipelineGraphQL | GetAnalysisInaccessibleInfoNode
): pipeline is AnalysisGraphQL {
  return pipeline.__typename === 'AnalysisNode';
}

export type AnalysisOutputStepType = (typeof ANALYSIS_OUTPUT_STEP_TYPES)[number];

export function isAnalysisOutputStepType(stepType: PipelineStepType): stepType is AnalysisOutputStepType {
  return ANALYSIS_OUTPUT_STEP_TYPES.includes(stepType as AnalysisOutputStepType);
}

export type NonExecutableStepType = (typeof NON_EXECUTABLE_STEP_TYPES)[number];

export function isNonExecutableStepType(stepType: PipelineStepType): stepType is NonExecutableStepType {
  return NON_EXECUTABLE_STEP_TYPES.includes(stepType as NonExecutableStepType);
}

/**
 * Step types that cannot be refreshed by users.
 *
 * This includes step types that aren't supported by the step execution library and step types that – while
 * technically rerunnable – don't make sense to refresh, since their output data wouldn't change.
 */
type NonRefreshableStepType = (typeof NON_REFRESHABLE_STEP_TYPES)[number];

export function isNonRefreshableStepType(stepType: PipelineStepType): stepType is NonRefreshableStepType {
  return NON_REFRESHABLE_STEP_TYPES.includes(stepType as NonRefreshableStepType);
}

/**
 * Array of step types that can be added to the analysis from the sidebar's step picker modal.
 *
 * Items not included are generally handled differently in the ui. For example, datasets initialization steps
 * are created through a different section in the sidebar.
 *
 * This is currently opt-in because there are many more non-addable steps than addable, but we might want to
 * switch to opt-out when that changes in the future.
 */
export const SIDEBAR_ADDABLE_STEP_TYPES = [PipelineStepType.THIRD_PARTY] as const;
export type SidebarAddableStepType = (typeof SIDEBAR_ADDABLE_STEP_TYPES)[number];

export function isSidebarAddableStepType(stepType: PipelineStepType): stepType is SidebarAddableStepType {
  return SIDEBAR_ADDABLE_STEP_TYPES.includes(stepType as SidebarAddableStepType);
}

const ANALYSIS_INPUT_SINGLE_DATASET_OUTPUT_STEP_TYPES = [
  PipelineStepType.CONNECTION_DATA_DATASET,
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  PipelineStepType.DATA_CATALOG_DATASET,
  PipelineStepType.INITIALIZE_DATASET,
  PipelineStepType.NOTEBOOK_TABLE_DATASET,
  PipelineStepType.PLATE_SCHEMA_DATASET,
  PipelineStepType.REGISTRY_SCHEMA_DATASET,
  PipelineStepType.TEMPLATE_INPUT,
  PipelineStepType.UPLOAD_CSV_DATASET,
] as const;

/**
 * Array of Dataset Creation steps shown in the top section of the sidebar.
 *
 * `THIRD_PARTY` is not currently thought of as a Dataset Creation step since it does not go into the
 * "Analysis Tables" section of the sidebar.
 *
 * @package exported for testing
 */
export const SIDEBAR_DATASET_INITIALIZATION_SINGLE_OUTPUT_STEP_TYPES = [
  ...ANALYSIS_INPUT_SINGLE_DATASET_OUTPUT_STEP_TYPES,
  PipelineStepType.JOIN,
  PipelineStepType.PROMOTE_DATASET,
  PipelineStepType.UNION_DATASETS,
  PipelineStepType.LOOK_UP,
  PipelineStepType.CREATE_DATASET_FROM_FILE,
  PipelineStepType.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT,
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
] as const;

type SidebarDatasetInitializationSingleOutputStepType =
  (typeof SIDEBAR_DATASET_INITIALIZATION_SINGLE_OUTPUT_STEP_TYPES)[number];

export function isSidebarDatasetInitializationSingleOutputStepType(
  stepType: PipelineStepType
): stepType is SidebarDatasetInitializationSingleOutputStepType {
  return SIDEBAR_DATASET_INITIALIZATION_SINGLE_OUTPUT_STEP_TYPES.includes(
    stepType as SidebarDatasetInitializationSingleOutputStepType
  );
}

/**
 * Steps that can start multiple new top-level tables, and don't have inputs.
 *
 * Parallel of `ANALYSIS_INPUT_SINGLE_DATASET_OUTPUT_STEP_TYPES`
 */
const ANALYSIS_INPUT_MULTI_OUTPUT_STEP_TYPES = [
  PipelineStepType.PROCEDURE_RUN_DATA_FRAMES,
  PipelineStepType.THIRD_PARTY,
];

/**
 * Steps that can start multiple new top-level tables.
 *
 * Parallel of `SIDEBAR_DATASET_INITIALIZATION_SINGLE_OUTPUT_STEP_TYPES`
 *
 * @package exported for testing
 */
export const SIDEBAR_DATASET_INITIALIZATION_MULTI_OUTPUT_STEP_TYPES = [
  ...ANALYSIS_INPUT_MULTI_OUTPUT_STEP_TYPES,
  PipelineStepType.USER_DEFINED_FUNCTION,
] as const;

type SidebarDatasetInitializationMultiOutputStepType =
  (typeof SIDEBAR_DATASET_INITIALIZATION_MULTI_OUTPUT_STEP_TYPES)[number];

export function isSidebarDatasetInitializationMultiOutputStepType(
  stepType: PipelineStepType
): stepType is SidebarDatasetInitializationMultiOutputStepType {
  return SIDEBAR_DATASET_INITIALIZATION_MULTI_OUTPUT_STEP_TYPES.includes(stepType);
}

const ANALYSIS_INPUT_STEP_TYPES = [
  ...ANALYSIS_INPUT_SINGLE_DATASET_OUTPUT_STEP_TYPES,
  ...ANALYSIS_INPUT_MULTI_OUTPUT_STEP_TYPES,
] as const;

type AnalysisInputStepType = (typeof ANALYSIS_INPUT_STEP_TYPES)[number];

export function isAnalysisInputStepType(stepType: PipelineStepType): stepType is AnalysisInputStepType {
  return ANALYSIS_INPUT_STEP_TYPES.includes(stepType);
}

export function getDataFrameSpecFromGetAnalysisDataFrame(dataFrame: GetAnalysisDataFrame): DataFrameSpec {
  return {
    columns: dataFrame.allColumnsJson,
    name: dataFrame.name,
  };
}
